/**
 * PM-5 — pure helpers shared by the wizard steps. Kept side-effect-free so
 * they unit-test without rendering (and so re-validate/commit body builders
 * in PM-6 can reuse them).
 */
import { overrideFieldKey } from "./overrideDiff.js";
import type {
  AnalyzeImportReport,
  ImportAnalyzedRow,
  ImportColumnMapping,
  ImportFieldDiff,
  ImportFieldKey,
  ImportRawRow,
  ImportValidationSeverity,
} from "./types.js";

/** Highest-priority severity present on a row (drives the row badge/colour).
 *  Order: hard_error > override_block > needs_confirm > info. */
const SEVERITY_RANK: Record<ImportValidationSeverity, number> = {
  hard_error: 3,
  override_block: 2,
  needs_confirm: 1,
  info: 0,
};

export function rowSeverity(
  row: ImportAnalyzedRow,
): ImportValidationSeverity | null {
  let worst: ImportValidationSeverity | null = null;
  for (const m of row.messages) {
    if (worst === null || SEVERITY_RANK[m.severity] > SEVERITY_RANK[worst]) {
      worst = m.severity;
    }
  }
  return worst;
}

/** Worst severity among messages targeting a specific field (cell colour). */
export function cellSeverity(
  row: ImportAnalyzedRow,
  field: string,
): ImportValidationSeverity | null {
  let worst: ImportValidationSeverity | null = null;
  for (const m of row.messages) {
    if (m.field !== field) continue;
    if (worst === null || SEVERITY_RANK[m.severity] > SEVERITY_RANK[worst]) {
      worst = m.severity;
    }
  }
  return worst;
}

/** Column index a given field is mapped to, or null when unmapped. */
export function columnForField(
  mapping: readonly ImportColumnMapping[],
  field: ImportFieldKey,
): number | null {
  const hit = mapping.find((m) => m.field === field);
  return hit ? hit.columnIndex : null;
}

/** The raw cell value of a mapped field on a row (post-edit; null if
 *  unmapped or empty). */
export function valueForField(
  row: ImportAnalyzedRow,
  mapping: readonly ImportColumnMapping[],
  field: ImportFieldKey,
): string | null {
  const col = columnForField(mapping, field);
  if (col === null) return null;
  return row.cells.find((c) => c.columnIndex === col)?.value ?? null;
}

/**
 * FB-31 — backfill each analyzed row's `cells` with the raw sheet value for
 * every MAPPED column, so a column the user mapped on the Data step actually
 * carries data into the re-validate (and onward to the Review tree).
 *
 * Root cause: the BE's analyzed `rows[].cells` only carry the columns that
 * were mapped at the time of the parse. When the user assigns a previously
 * unmapped / ignored raw column (e.g. column D → "Park") via the column-header
 * dropdown, that column has NO cell in the analyzed rows. The mapping change
 * re-validates with `rows = report.rows` (the analyzed rows), so the newly
 * mapped column points at a value that isn't there → `siteName` stays empty
 * and Review still says the park is unassigned.
 *
 * The raw value for that column DOES exist in `report.rawRows` (every column,
 * by raw `rowIndex`). For each analyzed row we therefore ensure a cell exists
 * for every column the new mapping targets, sourcing the value from the
 * matching raw row. An EXISTING analyzed cell is preserved verbatim (it may be
 * a user inline-edit, which must not be clobbered by the raw value); only
 * missing cells are added. Rows are returned unchanged when nothing needs
 * adding, so a no-op mapping change does not churn references.
 */
export function backfillRowCellsForMapping(
  rows: readonly ImportAnalyzedRow[],
  rawRows: readonly ImportRawRow[] | undefined,
  mapping: readonly ImportColumnMapping[],
): ImportAnalyzedRow[] {
  // The columns the mapping targets (a non-null field). Only these matter for
  // validation; ignored columns never need a backfilled value.
  const mappedColumns = new Set<number>();
  for (const m of mapping) {
    if (m.field !== null) mappedColumns.add(m.columnIndex);
  }
  if (mappedColumns.size === 0) return [...rows];

  const rawByRowIndex = new Map<number, ImportRawRow>();
  for (const rr of rawRows ?? []) rawByRowIndex.set(rr.rowIndex, rr);

  return rows.map((row) => {
    const present = new Set(row.cells.map((c) => c.columnIndex));
    const missing = [...mappedColumns].filter((ci) => !present.has(ci));
    if (missing.length === 0) return row;
    const raw = rawByRowIndex.get(row.rowIndex);
    const added = missing.map((columnIndex) => ({
      columnIndex,
      value:
        raw?.cells.find((c) => c.columnIndex === columnIndex)?.value ?? null,
    }));
    return { ...row, cells: [...row.cells, ...added] };
  });
}

/* ── Review tree (parks → turbines status) ─────────────────────────────── */

/** Per-node status in the Review tree. `error` = a hard_error blocks the row;
 *  `would-change` = an override_block diff against existing data; `exists` =
 *  the asset/site already exists (matched, no override); `will-create` = new.
 *  Priority for a node is error > would-change > exists > will-create. */
export const REVIEW_STATUSES = [
  "error",
  "would-change",
  "exists",
  "will-create",
] as const;
export type ReviewStatus = (typeof REVIEW_STATUSES)[number];

const REVIEW_STATUS_RANK: Record<ReviewStatus, number> = {
  error: 3,
  "would-change": 2,
  exists: 1,
  "will-create": 0,
};

/** The worst (highest-priority) of two review statuses (park rolls up its
 *  turbines). */
export function worstReviewStatus(
  a: ReviewStatus,
  b: ReviewStatus,
): ReviewStatus {
  return REVIEW_STATUS_RANK[a] >= REVIEW_STATUS_RANK[b] ? a : b;
}

/** Status of a single analyzed (turbine) row:
 *  - `error`        — any hard_error finding (gates the batch).
 *  - `would-change` — any override_block finding (diff vs existing data).
 *  - `exists`       — the row's `identifier` matched an existing asset.
 *  - `will-create`  — otherwise (a fresh turbine). */
export function turbineReviewStatus(row: ImportAnalyzedRow): ReviewStatus {
  if (row.messages.some((m) => m.severity === "hard_error")) return "error";
  if (row.messages.some((m) => m.severity === "override_block")) {
    return "would-change";
  }
  if (row.assetExists === true) return "exists";
  return "will-create";
}

export interface ReviewTurbineNode {
  rowIndex: number;
  /** The turbine identifier (mapped `identifier` cell), or a row fallback. */
  identifier: string;
  status: ReviewStatus;
  /** The override_block per-field diffs (PM-D18) when `status` is
   *  `would-change` — surfaced inline in the tree (FB-27). Empty otherwise. */
  diffs: ImportFieldDiff[];
  /** FB-32 — true when ANY of a `would-change` row's diff fields was marked by
   *  a tenant_admin to override (update base data from the sheet). Drives the
   *  "Will update" chip (vs the default "Keep existing"). */
  willUpdate: boolean;
  /** FB-32 — the diff FIELDS (override field keys) the admin marked to update
   *  from the sheet (PER-CELL). The tree lists these as "Will update: …" and
   *  the remaining diff fields stay "keep existing". */
  willUpdateFields: string[];
  /** FB-35 — the hard_error reason(s) for an `error` turbine, shown under the
   *  Error tag so the user knows WHY it's blocked (e.g. "Turbine identifier is
   *  required."). Empty unless `status === "error"`. */
  errorMessages: string[];
}

export interface ReviewParkNode {
  /** The park name (mapped `siteName` cell); "—" when unmapped/blank. */
  siteName: string;
  /** True when ANY included row in this park matched an existing site. */
  siteExists: boolean;
  /** Rolled-up status across the park's turbines OR the site's existence. */
  status: ReviewStatus;
  turbines: ReviewTurbineNode[];
}

/** Group the INCLUDED analyzed rows into a parks → turbines tree with a
 *  per-node status. A park's status is the worst of its turbines' statuses,
 *  but a park that is brand-new (no existing site) is at least `will-create`
 *  and one that matched an existing site reads `exists` when nothing diverges.
 *  Pure (no rendering) so it unit-tests directly. */
export function buildReviewTree(
  report: AnalyzeImportReport,
  overrideFieldKeys: ReadonlySet<string> = new Set<string>(),
): ReviewParkNode[] {
  const idCol =
    report.mapping.find((m) => m.field === "identifier")?.columnIndex ?? null;
  const siteCol =
    report.mapping.find((m) => m.field === "siteName")?.columnIndex ?? null;

  const cell = (row: ImportAnalyzedRow, col: number | null): string => {
    if (col === null) return "";
    return (row.cells.find((c) => c.columnIndex === col)?.value ?? "").trim();
  };

  const byPark = new Map<string, ReviewParkNode>();
  for (const row of report.rows) {
    if (!row.included) continue;
    const siteName = cell(row, siteCol) || "—";
    const identifier = cell(row, idCol) || `Row ${String(row.rowIndex + 1)}`;
    const turbineStatus = turbineReviewStatus(row);

    let park = byPark.get(siteName);
    if (park === undefined) {
      park = {
        siteName,
        siteExists: false,
        // Seed at the lowest rank; rolled up below.
        status: "will-create",
        turbines: [],
      };
      byPark.set(siteName, park);
    }
    const diffs: ImportFieldDiff[] =
      turbineStatus === "would-change"
        ? row.messages.flatMap((m) =>
            m.severity === "override_block" && m.diff ? m.diff : [],
          )
        : [];
    // FB-32 — the diff fields this admin marked PER CELL (not the whole row).
    // Distinct field keys, in diff order; the rest stay keep-existing.
    const seenFields = new Set<string>();
    const willUpdateFields: string[] = [];
    if (turbineStatus === "would-change") {
      for (const d of diffs) {
        if (seenFields.has(d.field)) continue;
        seenFields.add(d.field);
        if (overrideFieldKeys.has(overrideFieldKey(row.rowIndex, d.field))) {
          willUpdateFields.push(d.field);
        }
      }
    }
    const errorMessages =
      turbineStatus === "error"
        ? row.messages
            .filter((m) => m.severity === "hard_error")
            .map((m) => m.message)
        : [];
    park.turbines.push({
      rowIndex: row.rowIndex,
      identifier,
      status: turbineStatus,
      diffs,
      willUpdate: willUpdateFields.length > 0,
      willUpdateFields,
      errorMessages,
    });
    if (row.siteExists === true) park.siteExists = true;
    park.status = worstReviewStatus(park.status, turbineStatus);
  }

  // A matched-existing site with no diverging turbine reads `exists`.
  for (const park of byPark.values()) {
    if (park.siteExists && park.status === "will-create") {
      park.status = "exists";
    }
  }

  return [...byPark.values()];
}

/** Distinct, trimmed, non-empty `systemType` raw strings across INCLUDED
 *  rows that carry a `type_not_in_catalogue` finding — these need the
 *  create-new-model confirm (PM-D11). */
export function distinctNeedsConfirmTypes(
  report: AnalyzeImportReport,
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const row of report.rows) {
    if (!row.included) continue;
    const needs = row.messages.some(
      (m) =>
        m.severity === "needs_confirm" && m.code === "type_not_in_catalogue",
    );
    if (!needs) continue;
    const raw = valueForField(row, report.mapping, "systemType");
    const key = (raw ?? "").trim();
    if (key.length === 0 || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}
